import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdProjects } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Project } from '~hooks/useProjects/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

interface UseDiscardedProjectsQueryProps {
  companyId?: string;
  cursor?: string;
  direction?: PaginationLink;
}

/** Soft-deleted projects for the Settings > Deleted Records page. */
export default function useDiscardedProjectsQuery({
  companyId,
  cursor,
  direction,
}: UseDiscardedProjectsQueryProps) {
  const query = useQuery({
    queryKey: ['discardedProjects', companyId, direction, cursor],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const response = await getV1CompaniesCompanyIdProjects({
              path: { 'company-id': companyId },
              query: {
                'filter[discarded]': true,
                'page[limit]': MAX_API_PAGE_SIZE,
                ...(direction && cursor ? { [`page[${direction}]`]: cursor } : {}),
              },
              signal,
            });

            return {
              pagination: extractPagination(response),
              projects: response.data.data.map(Project.parse),
            };
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.projects.failed_to_fetch'),
            );
            throw error;
          }
        }
      : skipToken,
  });

  return {
    isFetching: query.isFetching,
    pagination: query.data?.pagination,
    projects: query.data?.projects ?? [],
  };
}
